<script setup lang="ts">
import type { Store } from '#shared/types/store'
import type { DealCard } from '#shared/types/deal'
import type { PaginatedResponse } from '#shared/types/api'

definePageMeta({ layout: 'default' })

const route = useRoute()
const slug = computed(() => route.params.slug as string)
const config = useRuntimeConfig()
const { trackEvent } = useUmami()
const pageUrl = computed(() => `${config.public.appUrl}${route.path}`)
const defaultShareImage = `${config.public.appUrl}/og-default.png`

const { data: store } = await useFetch<Store>(`/stores/${slug.value}`, {
  baseURL: config.public.apiBaseUrl,
  key: `store-${slug.value}`,
})

const seoTitle = computed(() =>
  store.value
    ? `Ofertas da ${store.value.name} — Promoções e Descontos | Descontito`
    : 'Loja — Descontito',
)

const seoDescription = computed(() => {
  if (!store.value) return undefined
  if (store.value.description) {
    return store.value.description.replace(/\s+/g, ' ').trim().slice(0, 155)
  }

  return `Confira as melhores ofertas, promoções e cupons da ${store.value.name} no Descontito.`
})

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
  ogTitle: seoTitle,
  ogDescription: seoDescription,
  ogType: 'website',
  ogUrl: pageUrl,
  ogImage: defaultShareImage,
  ogImageAlt: computed(() =>
    store.value
      ? `Ofertas da loja ${store.value.name} no Descontito`
      : 'Ofertas de lojas no Descontito',
  ),
  twitterTitle: seoTitle,
  twitterDescription: seoDescription,
  twitterImage: defaultShareImage,
  twitterImageAlt: computed(() =>
    store.value
      ? `Ofertas da loja ${store.value.name} no Descontito`
      : 'Ofertas de lojas no Descontito',
  ),
  twitterCard: 'summary_large_image',
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: pageUrl.value,
    },
  ],
  meta: [
    { property: 'og:image:secure_url', content: defaultShareImage },
    { property: 'og:image:type', content: 'image/png' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
  ],
})

const tabs = [
  { key: 'hot', label: '🔥 Em Alta', sort: 'hot' as const },
  { key: 'new', label: '🕐 Recentes', sort: 'new' as const },
  { key: 'commented', label: '💬 Mais Comentadas', sort: 'commented' as const },
]

const activeTab = ref('hot')
const activeOnly = ref(true)

const currentFilters = computed(() => ({
  store: slug.value,
  sort: tabs.find(t => t.key === activeTab.value)?.sort ?? 'hot',
  status: activeOnly.value ? 'ACTIVE' as const : undefined,
  limit: 20,
}))

const { data: initialDeals } = await useFetch<PaginatedResponse<DealCard>>('/deals', {
  baseURL: config.public.apiBaseUrl,
  query: currentFilters.value,
  key: `store-deals-initial-${slug.value}-${activeTab.value}-${activeOnly.value ? 'active' : 'all'}`,
})

const { deals, loadMore, hasMore, loading, reset } = useInfiniteDeals(currentFilters.value, initialDeals.value)
const schemaDeals = computed(() => deals.value.slice(0, 10))

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: computed(() => [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Inicio',
        item: config.public.appUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Lojas',
        item: `${config.public.appUrl}/`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: store.value?.name ?? 'Loja',
        item: pageUrl.value,
      },
    ]),
  }),
  {
    '@type': 'CollectionPage',
    name: seoTitle,
    description: seoDescription,
    url: pageUrl,
    inLanguage: 'pt-BR',
    about: computed(() =>
      store.value
        ? {
            '@type': 'Organization',
            name: store.value.name,
            url: store.value.websiteUrl,
          }
        : undefined,
    ),
  },
  {
    '@type': 'ItemList',
    name: computed(() =>
      store.value
        ? `Ofertas da loja ${store.value.name}`
        : 'Ofertas por loja',
    ),
    numberOfItems: computed(() => schemaDeals.value.length),
    itemListElement: computed(() =>
      schemaDeals.value.map((deal, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${config.public.appUrl}/oferta/${deal.slug}`,
        name: deal.title,
      })),
    ),
  },
])

const sentinel = ref<HTMLElement | null>(null)

onMounted(() => {
  if (!sentinel.value) return
  const observer = new IntersectionObserver(
    ([entry]) => { if (entry?.isIntersecting) loadMore() },
    { rootMargin: '200px' },
  )
  observer.observe(sentinel.value)
  onUnmounted(() => observer.disconnect())
})

watch(activeTab, (value) => {
  trackEvent('deal-sort-change', { page: 'store', store: slug.value, sort: value })
  reset(currentFilters.value)
})

watch(activeOnly, (value) => {
  trackEvent('active-filter-toggle', { page: 'store', store: slug.value, activeOnly: value })
  reset(currentFilters.value)
})
</script>

<template>
  <div>
    <div class="flex items-center gap-3 mb-4">
      <NuxtImg
        v-if="store?.logoUrl"
        :src="store.logoUrl"
        :alt="store?.name"
        class="w-12 h-12 object-contain rounded-lg border border-gray-200 dark:border-gray-700 p-1"
      />
      <div>
        <h1 class="text-xl font-bold text-gray-900 dark:text-white">
          {{ store?.name ?? 'Carregando...' }}
        </h1>
        <p v-if="store?.description" class="text-sm text-gray-500">{{ store.description }}</p>
      </div>
    </div>

    <!-- Tabs + filtro -->
    <div class="flex items-center justify-between gap-2 mb-6 flex-wrap">
      <div class="flex gap-1">
        <UButton
          v-for="tab in tabs"
          :key="tab.key"
          :color="activeTab === tab.key ? 'primary' : 'neutral'"
          :variant="activeTab === tab.key ? 'solid' : 'ghost'"
          size="sm"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </UButton>
      </div>
      <UButton
        :color="activeOnly ? 'primary' : 'neutral'"
        :variant="activeOnly ? 'soft' : 'ghost'"
        size="sm"
        :icon="activeOnly ? 'i-heroicons-check-circle' : 'i-heroicons-clock'"
        @click="activeOnly = !activeOnly"
      >
        {{ activeOnly ? 'Apenas ativas' : 'Todas' }}
      </UButton>
    </div>

    <DealGrid :deals="deals" :loading="loading && deals.length === 0" />

    <div v-if="loading && deals.length > 0" class="flex justify-center py-8">
      <UIcon name="i-heroicons-arrow-path" class="w-6 h-6 animate-spin text-primary-500" />
    </div>

    <!-- Sentinel para infinite scroll -->
    <div ref="sentinel" class="h-4" />

    <!-- Sem mais resultados -->
    <div v-if="!hasMore && deals.length > 0" class="text-center py-8 text-sm text-gray-400">
      Você chegou ao fim das ofertas 🎉
    </div>
  </div>
</template>
